import { GRAPH_DEFAULTS, MOTIVES_TYPES, SCALES } from "../Enums/index.js";
import { translateLegend } from "../translationHelper.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import { translate as t } from "../i18nBridge.js";
import dayjs from "dayjs";
import { orderComparisonData } from "../Legend/orderComparisonData.js";

const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export const yoyLegendToSortOrder = (
	label: string,
	derivedDocument: DocumentDO
) => {
	const yoyMatches = {
		"ΔY-": undefined,
		"Y-": undefined,
		ΔBud: 0.5,
		Bud: 0.5,
	};

	if (
		label === "Y" ||
		label === derivedDocument.date_comparison.comparisonLabel
	) {
		return 0;
	}

	for (const [key, value] of Object.entries(yoyMatches)) {
		if (label.startsWith(key)) {
			return value ?? parseInt(label.slice(key.length));
		}
	}

	for (const customPeriod of derivedDocument.date_comparison.customPeriods ??
		[]) {
		if (label === customPeriod.name || label === `Δ${customPeriod.name}`) {
			return dayjs(customPeriod.period.from_parsed).unix();
		}
	}

	return Number.MAX_SAFE_INTEGER;
};

export const legendOptions = (
	derivedDocument: DocumentDO,
	position: string = "bottom",
	isVisible: boolean = true,
	isDarkMode: boolean = false
) => {
	return {
		labels: {
			generateLabels: (chart) => {
				const ctx = chart.ctx;

				const property =
					derivedDocument.legend_dimension?.name_clean ??
					derivedDocument.vertical.name_clean;

				const legendTitle = t(`properties.${property}.title`) + ":";

				// ---- Measure title width ----
				ctx.save();
				ctx.font = {
					weight: 600,
					size: 13,
				};
				const titleWidth = ctx.measureText(legendTitle).width;
				ctx.restore();

				const uniqueLabels = new Set();

				let labels = chart.data.datasets
					.map((dataset, datasetIndex) => {
						let labelText = translateLegend(dataset.label);

						if (
							derivedDocument.legend_dimension?.granularity === "day" &&
							dateRegex.test(labelText)
						) {
							labelText = dayjs(labelText).format("DD MMM YYYY");
						}

						const isNoTargetLine = derivedDocument.graph_type !== "line";

						let backgroundColor =
							typeof dataset.backgroundColor === "function"
								? dataset.backgroundColor(null)
								: dataset.backgroundColor;

						if (Array.isArray(backgroundColor)) {
							backgroundColor = backgroundColor[0];
						}

						const pointStyle =
							isNoTargetLine &&
							!(dataset.renderAsLine || dataset.yAxisID === SCALES.Y_SECONDARY)
								? "circle"
								: "line";

						return {
							fontColor: isDarkMode
								? GRAPH_DEFAULTS.TEXT_COLOR_DARK
								: GRAPH_DEFAULTS.TEXT_COLOR,
							text: labelText,
							strokeStyle: isNoTargetLine
								? backgroundColor
								: dataset.borderColor,
							fillStyle: isNoTargetLine ? backgroundColor : dataset.borderColor,
							hidden: chart.getDatasetMeta(datasetIndex).hidden,
							pointStyle: pointStyle,
							isCapacity: dataset.motivesType === MOTIVES_TYPES.CAPACITY,
						};
					})
					.filter((label) => {
						if (!uniqueLabels.has(label.text)) {
							uniqueLabels.add(label.text);
							return true;
						}
						return false;
					});

				if (
					derivedDocument.date_comparison.periods?.length ||
					derivedDocument.date_comparison.customPeriods?.length
				) {
					labels = orderComparisonData(derivedDocument, labels, (l) => l.text);
				}

				// Move Capacity (incl. split normal/programmatic) to end
				labels = [
					...labels.filter((label) => !label.isCapacity),
					...labels.filter((label) => label.isCapacity),
				];

				if (position !== "top" && position !== "bottom") {
					// ---- Measure largest label ----
					ctx.save();
					ctx.font = {
						size: 12,
					};
					const maxLabelWidth = Math.max(
						...labels.map((label) => ctx.measureText(label.text).width)
					);
					ctx.restore();

					// ---- If title is wider than labels → pad labels ----
					if (titleWidth > maxLabelWidth) {
						const paddingWidth = titleWidth - maxLabelWidth;

						labels = labels.map((label) => {
							const currentWidth = ctx.measureText(label.text).width;
							const missing = titleWidth - currentWidth;

							if (missing > 0) {
								const spaceWidth = ctx.measureText(" ").width;
								const spacesNeeded = Math.ceil(missing / spaceWidth);

								return {
									...label,
									text: label.text + " ".repeat(spacesNeeded),
								};
							}

							return label;
						});
					}
				}

				return labels;
			},

			font: {
				size: 12,
			},

			usePointStyle: true,
		},
		position: position,
		align: position === "bottom" || position === "top" ? "center" : "start",
		title: {
			text: () => {
				const property =
					derivedDocument.legend_dimension?.name_clean ??
					derivedDocument.vertical.name_clean;
				return t(`properties.${property}.title`);
			},
			padding: {
				top: 12,
			},
			display: derivedDocument.legend_dimension?.name_clean,
			font: {
				weight: 500,
				size: 12,
			},
			color: GRAPH_DEFAULTS.TEXT_COLOR,
		},
		display: isVisible,
	};
};
